import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp, X } from 'lucide-react';
import {
  useFieldDefinitions,
  useCreateFieldDefinition,
  useUpdateFieldDefinition,
  useDeleteFieldDefinition,
  useReorderFieldDefinitions,
} from '../../hooks/useFieldDefinitions.js';
import { toast } from '../../lib/toast.js';
import { useWorkspaceStore } from '../../stores/workspace.js';
import { useFeatureFlag } from '../../lib/posthog.js';
import type { FieldDefinition, FieldEntityType, FieldType } from '@mayksel/types';
import type { FieldOption } from '@mayksel/types';

const FIELD_TYPES: FieldType[] = [
  'text', 'number', 'date', 'boolean',
  'select', 'multi_select', 'url', 'email', 'phone',
];

const ENTITY_TABS: FieldEntityType[] = ['company', 'contact', 'bucket', 'order', 'line_item'];

interface NewFieldState {
  label: string;
  key: string;
  type: FieldType;
  isRequired: boolean;
  options: FieldOption[];
}

const EMPTY_NEW_FIELD: NewFieldState = {
  label: '', key: '', type: 'text', isRequired: false, options: [],
};

function toKey(label: string): string {
  return label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').replace(/^(\d)/, '_$1');
}

export default function CustomFieldsSettings() {
  const { t } = useTranslation('customFields');
  const workspace = useWorkspaceStore((s) => s.workspace);
  const customFieldsEnabled = useFeatureFlag('custom_fields');

  const [activeTab, setActiveTab] = useState<FieldEntityType>('company');
  const [showForm, setShowForm] = useState(false);
  const [newField, setNewField] = useState<NewFieldState>(EMPTY_NEW_FIELD);
  const [newOptionLabel, setNewOptionLabel] = useState('');

  const { data: fields = [], isLoading } = useFieldDefinitions(activeTab);
  const createField = useCreateFieldDefinition();
  const updateField = useUpdateFieldDefinition();
  const deleteField = useDeleteFieldDefinition();
  const reorderFields = useReorderFieldDefinitions();

  if (!customFieldsEnabled) return null;

  const bucketLabel = workspace?.bucketLabel ?? 'Project';
  const orderLabel = workspace?.orderLabel ?? 'Order';

  function tabLabel(tab: FieldEntityType): string {
    if (tab === 'bucket') return bucketLabel + 's';
    if (tab === 'order') return orderLabel + 's';
    return t(`tabs.${tab}`);
  }

  async function handleCreate() {
    if (!newField.label.trim() || !newField.key.trim()) return;
    try {
      await createField.mutateAsync({
        entityType: activeTab,
        label: newField.label.trim(),
        key: newField.key.trim(),
        type: newField.type,
        isRequired: newField.isRequired,
        isActive: true,
        options: ['select', 'multi_select'].includes(newField.type) ? newField.options : undefined,
      });
      toast.success(t('toast.created'));
      setNewField(EMPTY_NEW_FIELD);
      setShowForm(false);
      setNewOptionLabel('');
    } catch {
      toast.error(t('toast.create_error'));
    }
  }

  async function handleToggleActive(field: FieldDefinition) {
    try {
      await updateField.mutateAsync({ id: field.id, data: { isActive: !field.isActive } });
    } catch {
      toast.error(t('toast.update_error'));
    }
  }

  async function handleDelete(field: FieldDefinition) {
    if (!window.confirm(t('delete_confirm', { label: field.label }))) return;
    try {
      await deleteField.mutateAsync(field.id);
      toast.success(t('toast.deleted'));
    } catch {
      toast.error(t('toast.delete_error'));
    }
  }

  async function handleReorder(fromIdx: number, toIdx: number) {
    const reordered = [...fields];
    const [moved] = reordered.splice(fromIdx, 1);
    if (!moved) return;
    reordered.splice(toIdx, 0, moved);
    try {
      await reorderFields.mutateAsync({ ids: reordered.map((f) => f.id) });
    } catch {
      toast.error(t('toast.update_error'));
    }
  }

  function addOption() {
    const trimmed = newOptionLabel.trim();
    if (!trimmed) return;
    const value = trimmed.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    setNewField((prev) => ({
      ...prev,
      options: [...prev.options, { label: trimmed, value }],
    }));
    setNewOptionLabel('');
  }

  const needsOptions = newField.type === 'select' || newField.type === 'multi_select';

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text)', marginBottom: 'var(--space-1)' }}>
          {t('title')}
        </h2>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          {t('subtitle')}
        </p>
      </div>

      {/* Entity type tabs */}
      <div style={{ display: 'flex', gap: 'var(--space-1)', marginBottom: 'var(--space-5)', borderBottom: '1px solid var(--color-border)', paddingBottom: 'var(--space-1)' }}>
        {ENTITY_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setShowForm(false); setNewField(EMPTY_NEW_FIELD); }}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              fontSize: 'var(--text-sm)',
              fontWeight: activeTab === tab ? 'var(--font-medium)' : undefined,
              color: activeTab === tab ? 'var(--color-brand)' : 'var(--color-text-secondary)',
              borderBottom: activeTab === tab ? '2px solid var(--color-brand)' : '2px solid transparent',
              background: 'none', border: 'none', cursor: 'pointer',
            }}
          >
            {tabLabel(tab)}
          </button>
        ))}
      </div>

      {/* Field list */}
      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="skeleton-line" style={{ height: 48 }} />
          ))}
        </div>
      ) : fields.length === 0 && !showForm ? (
        <div style={{ textAlign: 'center', padding: 'var(--space-12) var(--space-4)', border: '1px dashed var(--color-border)', borderRadius: 'var(--radius-lg)' }}>
          <p style={{ fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-1)' }}>
            {t('no_fields')}
          </p>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            {t('no_fields_sub')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
          {fields.map((field, idx) => (
            <div
              key={field.id}
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-3) var(--space-4)',
                background: field.isActive ? 'var(--color-bg)' : 'var(--color-bg-subtle)',
                opacity: field.isActive ? 1 : 0.6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                {/* Reorder handles */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button
                    onClick={() => idx > 0 && handleReorder(idx, idx - 1)}
                    disabled={idx === 0}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: idx === 0 ? 'default' : 'pointer', color: 'var(--color-text-muted)', opacity: idx === 0 ? 0.3 : 1 }}
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    onClick={() => idx < fields.length - 1 && handleReorder(idx, idx + 1)}
                    disabled={idx === fields.length - 1}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: idx === fields.length - 1 ? 'default' : 'pointer', color: 'var(--color-text-muted)', opacity: idx === fields.length - 1 ? 0.3 : 1 }}
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>
                <GripVertical size={14} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />

                {/* Field info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <span style={{ fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
                      {field.label}
                    </span>
                    {field.isRequired && (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-danger)', fontWeight: 'var(--font-medium)' }}>
                        {t('required_badge')}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    {t(`field_types.${field.type}`)} · <code style={{ fontSize: 'var(--text-xs)' }}>{field.key}</code>
                  </div>
                </div>

                {/* Actions */}
                <button
                  onClick={() => handleToggleActive(field)}
                  style={{
                    fontSize: 'var(--text-xs)', padding: '2px var(--space-2)',
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                    background: 'none', cursor: 'pointer',
                    color: field.isActive ? 'var(--color-text-secondary)' : 'var(--color-brand)',
                  }}
                >
                  {field.isActive ? t('deactivate') : t('activate')}
                </button>
                <button
                  onClick={() => handleDelete(field)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: 'var(--space-1)' }}
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Options preview for select fields */}
              {field.options && field.options.length > 0 && (
                <div style={{ marginTop: 'var(--space-2)', marginLeft: 72, display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                  {field.options.map((opt) => (
                    <span key={opt.value} style={{
                      fontSize: 'var(--text-xs)', padding: '1px var(--space-2)',
                      border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                      color: 'var(--color-text-secondary)',
                    }}>
                      {opt.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New field form */}
      {showForm ? (
        <div style={{
          border: '1px solid var(--color-brand)', borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)', background: 'var(--color-bg)',
        }}>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
            {/* Label */}
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                {t('field_label')}
              </label>
              <input
                type="text"
                value={newField.label}
                onChange={(e) => {
                  const label = e.target.value;
                  setNewField((prev) => ({
                    ...prev,
                    label,
                    key: prev.key === toKey(prev.label) ? toKey(label) : prev.key,
                  }));
                }}
                placeholder={t('label_placeholder')}
                autoFocus
                style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', color: 'var(--color-text)', background: 'var(--color-bg)' }}
              />
            </div>

            {/* Key */}
            <div style={{ flex: '1 1 160px' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                {t('field_key')}
              </label>
              <input
                type="text"
                value={newField.key}
                onChange={(e) => setNewField((prev) => ({ ...prev, key: e.target.value }))}
                placeholder="e.g. my_field"
                style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', fontFamily: 'monospace', color: 'var(--color-text)', background: 'var(--color-bg)' }}
              />
            </div>

            {/* Type */}
            <div style={{ flex: '1 1 140px' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                {t('field_type')}
              </label>
              <select
                value={newField.type}
                onChange={(e) => setNewField((prev) => ({ ...prev, type: e.target.value as FieldType, options: [] }))}
                style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', color: 'var(--color-text)', background: 'var(--color-bg)' }}
              >
                {FIELD_TYPES.map((ft) => (
                  <option key={ft} value={ft}>{t(`field_types.${ft}`)}</option>
                ))}
              </select>
            </div>

            {/* Required */}
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newField.isRequired}
                  onChange={(e) => setNewField((prev) => ({ ...prev, isRequired: e.target.checked }))}
                />
                {t('field_required')}
              </label>
            </div>
          </div>

          {/* Options for select/multi_select */}
          {needsOptions && (
            <div style={{ marginBottom: 'var(--space-3)' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                {t('field_options')}
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                {newField.options.map((opt, i) => (
                  <span key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: 'var(--text-xs)', padding: '2px var(--space-2)',
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                    color: 'var(--color-text-secondary)',
                  }}>
                    {opt.label}
                    <button
                      onClick={() => setNewField((prev) => ({ ...prev, options: prev.options.filter((_, j) => j !== i) }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-text-muted)', display: 'flex' }}
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <input
                  type="text"
                  value={newOptionLabel}
                  onChange={(e) => setNewOptionLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                  placeholder={t('option_placeholder')}
                  style={{ flex: 1, padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', color: 'var(--color-text)', background: 'var(--color-bg)' }}
                />
                <button
                  onClick={addOption}
                  style={{ padding: 'var(--space-2) var(--space-3)', background: 'var(--color-bg-subtle)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
                >
                  {t('add_option')}
                </button>
              </div>
            </div>
          )}

          {/* Form actions */}
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setShowForm(false); setNewField(EMPTY_NEW_FIELD); setNewOptionLabel(''); }}
              style={{ padding: 'var(--space-2) var(--space-4)', background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleCreate}
              disabled={!newField.label.trim() || !newField.key.trim() || createField.isPending}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                background: 'var(--color-brand)', color: 'var(--color-text-inverse)',
                border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)', cursor: 'pointer',
                opacity: (!newField.label.trim() || !newField.key.trim()) ? 0.5 : 1,
              }}
            >
              {t('add_field')}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-4)',
            border: '1px dashed var(--color-border)', borderRadius: 'var(--radius-md)',
            background: 'none', cursor: 'pointer',
            fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)',
            width: '100%', justifyContent: 'center',
          }}
        >
          <Plus size={16} />
          {t('add_field')}
        </button>
      )}
    </div>
  );
}
